// ---- formatting helpers ----

export function formatDuration(sec) {
  if (sec == null || isNaN(sec)) return "—";
  sec = Math.round(sec);
  if (sec >= 3600) {
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    return `${h}h ${m}m`;
  }
  if (sec >= 60) {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return s ? `${m}m ${s}s` : `${m}m`;
  }
  return `${sec}s`;
}

export function mmss(sec) {
  if (sec == null || isNaN(sec)) return "0:00";
  sec = Math.max(0, Math.round(sec));
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

// All call/activity timestamps from the backend are UTC. RingCentral records are shown in UK local
// time, so we must render in Europe/London (which applies BST/GMT automatically) — otherwise a 09:58
// UTC call shows as 09:58 instead of 10:58 BST. A timezone-less datetime string (backend serialises
// naive UTC) is treated as UTC by appending 'Z'; pure date strings (e.g. "2026-06-29") are left alone.
const UK_TZ = "Europe/London";

function asUTC(iso) {
  if (typeof iso === "string" && iso.includes("T") && !/([zZ]|[+-]\d{2}:?\d{2})$/.test(iso)) {
    return new Date(iso + "Z");
  }
  return new Date(iso);
}

function ukDayKey(d) {
  // YYYY-MM-DD as it falls in the UK timezone (sortable, locale-stable).
  return d.toLocaleDateString("en-CA", { timeZone: UK_TZ });
}

function ukTime(d) {
  // "9:58 AM" in UK time.
  return d.toLocaleTimeString("en-US", { timeZone: UK_TZ, hour: "numeric", minute: "2-digit", hour12: true });
}

export function relativeDate(iso) {
  if (!iso) return "—";
  const d = asUTC(iso);
  if (isNaN(d.getTime())) return "—";
  const that = ukDayKey(d);
  if (that === ukDayKey(new Date())) return `Today, ${ukTime(d)}`;
  if (that === ukDayKey(new Date(Date.now() - 86400000))) return `Yesterday, ${ukTime(d)}`;
  const [yy, mo, dd] = that.split("-");
  return `${dd}/${mo}/${yy}, ${ukTime(d)}`;
}

export function shortDate(iso) {
  if (!iso) return "—";
  const d = asUTC(iso);
  if (isNaN(d.getTime())) return "—";
  const [yy, mo, dd] = ukDayKey(d).split("-");
  return `${dd}/${mo}/${yy}`;
}

export function initials(name) {
  if (!name) return "??";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function scoreColor(score) {
  if (score == null) return "#9ca3af";
  if (score < 2.5) return "#f59e0b";
  if (score < 3.5) return "#eab308";
  return "#10b981";
}

export function scoreColorHard(score) {
  // 1-2 amber/red, 3 yellow, 4-5 green
  if (score == null) return "#9ca3af";
  if (score <= 1.5) return "#ef4444";
  if (score <= 2.5) return "#f59e0b";
  if (score <= 3.5) return "#eab308";
  return "#10b981";
}

export const ACTIVITY_TYPES = [
  "Outbound - Acquisition",
  "Outbound - In Life",
  "Inbound - Call From Customer",
  "Proposal",
  "Service Call",
  "Voicemail",
  "Teams Meeting",
];

export function isTeamsMeeting(call) {
  if (!call) return false;
  return call.activity_type === "Teams Meeting" || (!call.from_number && !call.to_number);
}

export function callTitle(call) {
  if (isTeamsMeeting(call)) return call.customer_name || "Teams Meeting";
  const num = call.direction === "inbound" ? call.from_number : call.to_number;
  const word = call.direction === "inbound" ? "from" : "to";
  return `Call ${word} ${num || "unknown"}`;
}

// ---- CSV export (shared) ----
// Lifted out of pages/CompanyDirectory.jsx unchanged (Salem, 2026-09-26: "we need the ability to
// export to CSV for any prospect list we can find so the managers can send it to BCs or Sales
// Reps to call") so every Prospecting page can offer the same one-click export of what's on
// screen, instead of each page inventing its own CSV writer.
export function csvEscape(v) {
  const s = v === null || v === undefined ? "" : String(v);
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function downloadCsv(columns, rows, filename) {
  const lines = [columns.join(",")];
  rows.forEach((r) => lines.push(columns.map((c) => csvEscape(r[c])).join(",")));
  // BOM so Excel opens £ and accented names correctly on a UK machine.
  const blob = new Blob(["﻿" + lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
